import React, { FC, forwardRef, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Text } from '@src/atoms/Text';
import { Icon } from '@src/atoms/Icon';
import { ISidebarMenuItemNestedProps, ISidebarMenuItemProps } from '../models';
import { useHasUserTag } from '@src/hooks/useHasUserTag';
import { SidebarMenuLink } from '../_Link/SidebarMenuLink';
import { Tooltip } from '@src/atoms/Tooltip';
import { Box } from '@src/atoms/Box';
import { Collapse } from '@src/atoms/Collapse';
import { BreakpointsContext } from '@src/providers/Breakpoints';

import styled from './SidebarMenuItem.scss';
import { classnames } from '@src/utils/classnames';

export const SidebarMenuItemNested = (props: ISidebarMenuItemNestedProps) => {
  const { nested, name, isNestedOpened, rounded, noOfLines, ...rest } = props;
  const [isOpen, setOpen] = useState(isNestedOpened);
  const groupClass = classnames(styled.rootNested, {
    [styled.rootNestedOpened]: isOpen,
    [styled.rootNestedRounded]: rounded,
  });

  const handleToggle = useCallback(() => {
    setOpen((o) => !o);
  }, []);

  return (
    <Box column>
      <Box gap={4} fullWidth onClick={handleToggle} className={groupClass} alignItems="center">
        <Text
          id={`rootNested_${props.id}`}
          noOfLines={noOfLines}
          size={props.theme === 'white' ? 'secondary' : undefined}
        >
          {name}
        </Text>
        <Icon name="triangle" size={16} view="basicGreySemiDark" className={styled.rootNestedIcon} />
      </Box>
      <Collapse open={isOpen}>
        <Box column fullWidth className={styled.nestedWrapper}>
          {nested.map((item) => (
            <SidebarMenuItem
              key={item.id}
              {...rest}
              {...item}
              isNestedOpened={isNestedOpened}
              rounded={rounded}
              noOfLines={noOfLines}
            />
          ))}
        </Box>
      </Collapse>
    </Box>
  );
};

const SidebarMenuItemLink: FC<ISidebarMenuItemProps> = forwardRef((props, ref) => {
  const [isTooltip, setIsTooltip] = useState(false);
  const textRef = useRef(null);
  const { isMobile } = useContext(BreakpointsContext);

  const handleSetTooltip = useCallback((e: HTMLElement | null) => {
    if (e) {
      setIsTooltip(e.offsetWidth < e.scrollWidth || e.offsetHeight < e.scrollHeight);
    }
  }, []);

  useEffect(() => {
    if (textRef.current) {
      const resizeObserver = new ResizeObserver((entries) => {
        if (entries[0]) {
          handleSetTooltip(entries[0].target as HTMLElement);
        }
      });

      resizeObserver.observe(textRef.current);

      return () => {
        resizeObserver.disconnect();
      };
    }
  }, []);

  return (
    <Tooltip content={props.name} open={isTooltip ? undefined : false} placement={isMobile ? 'top' : 'right'}>
      <SidebarMenuLink {...props} ref={ref}>
        <Text
          ref={textRef}
          id={props.id}
          noOfLines={props.noOfLines}
          size={props.theme === 'white' ? 'secondary' : undefined}
        >
          {props.name}
        </Text>
        {props.icon && <Icon name={props.icon} view={'secondaryYellow'} size="sm" />}
      </SidebarMenuLink>
    </Tooltip>
  );
});

export const SidebarMenuItem: FC<ISidebarMenuItemProps> = (props) => {
  const { tags = [], nested = [] } = props;
  const isEnableFromTags = useHasUserTag(tags);
  const isEnableLink = tags.length ? isEnableFromTags : true;

  if (!isEnableLink) return null;
  if (nested.length > 0) return <SidebarMenuItemNested {...props} nested={nested} />;

  return <SidebarMenuItemLink {...props} />;
};
